import { OrderPanelProps } from './index';
import React, { FC, memo, useEffect, useState } from 'react';
import { ConfigLeftSection, ConfigMainGrid, ConfigRightSection, StyledGrid } from '@components/Grid';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useTranslations } from '@hooks/useTranslations';
import { HandledComponentError } from '@components/ErrorHandling';
import { Benefits } from '@components/Benefits';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { Icon, Icons } from '@components/Icon';
import IconButton from '@components/IconButton';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import PriceSuffix from '@components/PriceSuffix';
import { usePrices } from '@hooks/usePriceCalculator';
import {
    getFinanceDetailText,
    getFinanceDetailView,
    getMonthlyPriceObjByType,
    renderPromotionalText,
} from '@utils/Price.utils';
import { StockFormatedPriceType } from 'src/services/stock/models/stockItem.price.model';
import { isBrandAC, isBrandAP, isBrandDS, isBrandOV, isMarketGB, ModalVersion } from 'src/constants/main';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { getFormattedOfferValidityDate } from '@utils/Car.utils';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { useDispatch, useSelector } from 'react-redux';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import routes from 'src/constants/routes';
import { BRAND, CarJourneyType } from 'src/services';
import GlobalDuck from 'src/redux/global/global.duck';
import { useRouter } from 'next/router';
import { Pages } from 'src/types/pages';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { ORDER_PANEL_ID, useOrderPanelHeight } from '@hooks/useOrderPanelHeight';
import MonthlyPriceLabel, { AprSizes } from '@components/MonthlyPriceLabel';
import { PriceVatExcluded } from '@components/PriceVatExcluded';
import { usePriceBoxDetails } from '@hooks/usePriceDetails';
import { useIsShowPriceExcludedVAT } from '@hooks/useIsShowPriceExcludedVAT';
import { IGlobalState } from 'src/redux/redux.interface';
import { usePlausible, UserEvent } from '@hooks/usePlausible';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import Button from '@components/Button';
import Link from "@components/Link";

const OrderPanelTemplate: FC<OrderPanelProps> = memo(
    ({ className, onSalesmanClick, onContinueBasketClick, onInfoIconClick, page }) => {
        try {
            const { t } = useTranslations();
            const { formatMoney } = useMoneyFormatter();
            const dispatch = useDispatch();
            const router = useRouter();

            const [phoneVisible, setPhoneVisible] = useState(false);

            const { carDetails, defaultConfiguration } = useCarDetailsDuck();
            const { globalTexts, pricesList } = useFinanceWidgetDuck();
            const isShowPriceExcludedVAT: boolean = useIsShowPriceExcludedVAT();
            const { carJourney } = useGlobalDuck();
            const { plausibleUserEvent } = usePlausible();
            const { cash, finance, catalog } = usePrices(
                carDetails,
                true,
                null,
                // We should ignore default configuration if order panel is NOT used on trim page
                page !== Pages.trim
            );
            const { totalPrice: baseTotalPrice, totalBasicPriceWithoutVat } = cash;
            const {
                totalPrice: baseMonthlyPrice,
                financeProperties: { deposit, apr, hasMonthlyPrices },
            } = finance;
            const { totalPrice: baseCatalogPrice } = catalog;

            const financeDetailView = getFinanceDetailView(carDetails?.prices, StockFormatedPriceType.EMPLOYEE);
            const financeDetailText = getFinanceDetailText(
                carDetails?.prices,
                StockFormatedPriceType.EMPLOYEE,
                globalTexts,
                pricesList,
                carDetails.externalId || carDetails.vehicleId
            );

            const showOfferValidityDate: boolean =
                useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_OFFER_VALIDITY_DATE_ENABLED) &&
                !useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_FINANCE_DISABLED) &&
                hasMonthlyPrices;

            const {
                isCataloguePriceEqualToStorePrice,
                showFOIcon,
                showFOLink,
                isCataloguePriceFeatureSwitchEnabled,
                isFinancePriceAvailable,
            } = usePriceBoxDetails({
                catalogPrice: baseCatalogPrice,
                basePrice: baseTotalPrice,
                financeDetailView,
                monthlyPrice: baseMonthlyPrice,
            });

            const offerValidityDate = getFormattedOfferValidityDate(carDetails?.importInputs);
            const offerValidityDateString =
                showOfferValidityDate && offerValidityDate
                    ? `${t('basket.offerValidityDate')} ${offerValidityDate}`
                    : '';
            const amountRounding = getMonthlyPriceObjByType(
                carDetails?.prices,
                StockFormatedPriceType.EMPLOYEE
            )?.amountRounding;

            // We should use prices from default configuration if order panel is used on trim page
            const carDetailsForPrices = page === Pages.trim ? defaultConfiguration || carDetails : carDetails;
            const extColor = carDetailsForPrices.exteriorColourCode || carDetailsForPrices.exteriorColour?.id;
            const intColor = carDetailsForPrices.interiorColourCode || carDetailsForPrices.interiorColour?.id;
            const { processCalculateSummaryDataPromise } = useSummaryDataPromise({
                carConfigId:
                    carDetailsForPrices?.id ||
                    carDetailsForPrices?._id ||
                    `${carDetailsForPrices.lcdv16}+${extColor}+${intColor}`,
            });

            const contactClick = () => {
                plausibleUserEvent(UserEvent.HelpNeeded);
                dispatch(
                    UIDuck.openModal({
                        data: {},
                        modalType: UIDuck.MODAL_TYPES.NEED_HELP,
                        modalVersion: ModalVersion.v2,
                    })
                );
            };

            const handleCustomizeClick = () => {
                const currentPathParams = extractParamsFromPathname(router.asPath);
                const newPathParams = {
                    nameplateBodyStyleSlug: currentPathParams.nameplateBodyStyleSlug,
                    speckPackSlug: carDetails?.specPackSlug,
                    engineGearboxFuelSlug: carDetails?.engineGearboxFuelSlug,
                    exteriorColourSlug: (defaultConfiguration || carDetails)?.exteriorColourSlug,
                    interiorColourSlug: (defaultConfiguration || carDetails)?.interiorColourSlug,
                };

                const modelURL = buildParameterizedPathname(routes.CAR, carJourney, newPathParams);

                return router.push(modelURL);
            };

            useOrderPanelHeight(carDetails?.extraFields?.promotionalText);

            useEffect(() => {
                const headerElement = document.getElementsByClassName('header-container')?.[0];

                window?.addEventListener('scroll', () => {
                    setPhoneVisible(window?.scrollY > headerElement.clientHeight);
                });
            }, []);

            return (
                <div
                    className={`${className} ${isShowPriceExcludedVAT ? 'withShowPriceExcludedVAT' : ''}`}
                    id={ORDER_PANEL_ID}
                >
                    <StyledGrid brand={BRAND} noPadding isWide>
                        <ConfigMainGrid isWide>
                            <ConfigLeftSection>
                                <div className="configLeftSectionContainer">
                                    <div className="priceSection">
                                        <div className="catalogPrice">
                                            {isCataloguePriceFeatureSwitchEnabled &&
                                                !isCataloguePriceEqualToStorePrice &&
                                                baseCatalogPrice && (
                                                    <>
                                                        <span className="catalogPriceLabel">
                                                            {t('price.catalog.title')}{' '}
                                                        </span>
                                                        <span className="catalogPriceAmount">
                                                            {formatMoney(baseCatalogPrice, true)}
                                                        </span>
                                                        <PriceSuffix hideAsterisk />
                                                        <span> | </span>
                                                    </>
                                                )}
                                            <strong>{t('offerListItem.specialPriceTextFrom')}</strong>
                                        </div>
                                        <div
                                            className={`price ${
                                                isShowPriceExcludedVAT ? 'showPriceExcludedVAT-container' : ''
                                            }`}
                                        >
                                            <div
                                                className={`cashPrice ${
                                                    isShowPriceExcludedVAT ? 'showPriceExcludedVAT' : ''
                                                }`}
                                                data-testid="TESTING_SELECTOR_CASH_PRICE"
                                            >
                                                <span>{formatMoney(baseTotalPrice, true)}</span>
                                                <PriceSuffix checkPrioritizedTranslation hideAsterisk />
                                            </div>
                                            {isShowPriceExcludedVAT && (
                                                <PriceVatExcluded
                                                    price={formatMoney(totalBasicPriceWithoutVat, true)}
                                                />
                                            )}
                                            {isFinancePriceAvailable && (
                                                <>
                                                    <div className="priceConjunction">
                                                        {t('offerListItem.financePrice.conjunction')}
                                                    </div>
                                                    <div className="monthlyPrice">
                                                        <div
                                                            className="price"
                                                            data-testid="TESTING_SELECTOR_FINANCE_PRICE"
                                                        >
                                                            <span>
                                                                {formatMoney(
                                                                    baseMonthlyPrice,
                                                                    false,
                                                                    '',
                                                                    amountRounding
                                                                )}
                                                            </span>
                                                            <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                                            {showFOIcon && (
                                                                <IconButton
                                                                    className="infoIcon"
                                                                    icon={
                                                                        isBrandAC ||
                                                                        isBrandAP ||
                                                                        (isBrandOV && !isMarketGB) ||
                                                                        isBrandDS
                                                                            ? Icons.InfoCircle
                                                                            : Icons.Info
                                                                    }
                                                                    width={14}
                                                                    height={14}
                                                                    dataTestId="TESTING_SELECTOR_FINANCE_PRICE_INFO_ICON"
                                                                    onClick={() => {
                                                                        processCalculateSummaryDataPromise();
                                                                        onInfoIconClick(financeDetailText);
                                                                    }}
                                                                    altId={`svgImgSeeFinanceDetails-${carDetails?.id}`}
                                                                    altText={financeDetailText}
                                                                />
                                                            )}
                                                        </div>
                                                        {!isMarketGB && (
                                                            <div className="deposit">
                                                                <p>
                                                                    {isFinancePriceAvailable &&
                                                                        renderPromotionalText(
                                                                            carDetails?.extraFields?.promotionalText,
                                                                            t('price.pattern'),
                                                                            deposit,
                                                                            null,
                                                                            null,
                                                                            null,
                                                                            offerValidityDateString,
                                                                            { apr }
                                                                        )}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {hasMonthlyPrices && showFOLink && (
                                                            <MonthlyPriceLabel
                                                                withPrimaryColor={isMarketGB}
                                                                size={AprSizes.SMALL}
                                                                aprValue={apr}
                                                                calculateSummaryData={
                                                                    processCalculateSummaryDataPromise
                                                                }
                                                                financeDetailText={financeDetailText}
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="benefitSection desktop">
                                        <Benefits offers={carDetails?.offers} />
                                    </div>
                                </div>
                            </ConfigLeftSection>
                            <ConfigRightSection unsetMaxWidth>
                                <div className="buttonSection">
                                    <div className="desktopContactSection">
                                        <Link
                                            label={t('configurator.contactSalesman')}
                                            onClick={contactClick}
                                            dataTestId="TESTING_CONFIG_CONTACT_SALESMAN_LINK"
                                            withArrowIcon
                                            primary
                                        />
                                        {phoneVisible && (
                                            <a href={`tel:${t('support.telephone')}`} className="phoneLink">
                                                {t('support.telephone')}
                                            </a>
                                        )}
                                    </div>
                                    <Button
                                        onClick={() => {
                                            handleCustomizeClick();
                                            if (onContinueBasketClick) onContinueBasketClick();
                                        }}
                                        primary
                                        data-testid="TESTING_TO_CONFIG_BOX_ORDERPANEL"
                                    >
                                        {t('orderPanel.button.label.basket')}
                                    </Button>
                                </div>
                            </ConfigRightSection>
                        </ConfigMainGrid>
                    </StyledGrid>
                    <div className="mobileContactSection" onClick={contactClick}>
                        <span className="label">
                            {t('header.nav.needHelp')} <Icon name={Icons.ChevronRight} width={11} height={11} />
                        </span>
                        <span className="phone">
                            <Icon
                                name={isBrandOV && !isMarketGB ? Icons.PhoneV2 : Icons.Phone}
                                width={24}
                                height={24}
                            />{' '}
                            {t('support.telephone')}
                        </span>
                    </div>
                </div>
            );
        } catch (e: any) {
            return <HandledComponentError error={e} />;
        }
    }
);
OrderPanelTemplate.displayName = 'OrderPanelTemplate';
export default OrderPanelTemplate;
